import React, { useCallback, useEffect, useState } from "react";
import { Col, Image, Row } from "react-bootstrap";
import { AdServerItems } from "./adserver-items";
import { Button } from "react-bootstrap";
import AdPreviewImage from "../../assets/images/adPreview1.png";
import plusIcon from "../../assets/images/icons/plus.svg";
import * as Styled from "./AdServerComponent.styled";
import { RollAdsModal } from "./roll-ads/roll-ads-modal";
import { RollAds } from "./roll-ads";
import { adServerService } from "../../services";
import { Loader } from "../ui";
import { Error } from "../../libs";
import { toast } from "react-toastify";
import { Campaign } from "../../libs/models/ad-server";

export const AdServerComponent: React.FC = () => {
  const [activeTabName, setActiveTabName] = useState("banners");
  const [showModal, setShowModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [, setCampaignList] = useState<Campaign[]>(campaigns);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adServerService.getAllCampaigns();
      
      if (response && Array.isArray(response.campaigns)) {
        const formattedCampaigns = response.campaigns.map((campaign) => ({
          campaignId: campaign.campaignId,
          visibility: campaign.visibility,
          title: campaign.campaignName || "Unknown",
          videos: `(${campaign.adCount || 0} videos)`,
          categories: campaign.categories || [],
          createdAt: campaign.createdAt || "",
        }));
        setCampaigns(formattedCampaigns);
      } else {
        setError("No campaigns found.");
        toast.error("No campaigns found."); 
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load campaigns.";
      setError(errorMessage);
      toast.error(errorMessage); 
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteCampaign = async (campaignId: string) => {
    try {
      const response = await adServerService.deleteCampaigns(campaignId);
      if (response && response.data.deletedCampaign) {
        toast.success(response.message || "Campaign deleted successfully!");
        setCampaignList((prev) => prev.filter((c) => c.campaignId !== campaignId));
        fetchCampaigns();
      }
    } catch (error: any) {
      console.error("Error deleting campaign:", error);
      toast.error(error.response?.data?.error?.message || "Failed to delete campaign. Please try again.");
    }
  };

  useEffect(() => {
    if (activeTabName === "rollads") fetchCampaigns();
  }, [activeTabName, fetchCampaigns]);

  const hasCampaigns = campaigns.length > 0;

  return (
    <Styled.AdServerContainer fluid className="pt-10">
      <Styled.AdServerRow>
        <Styled.AdDetailsCol md={activeTabName === "banners" ? 8 : 12}>
          <Row>
            <Styled.WelcomeHeaderWrapper>
              <Styled.WelcomeHeader>AdServer</Styled.WelcomeHeader>
              <Styled.AdServerTabsContainer>
                <Styled.AdServerTabTitle
                  className={activeTabName === "banners" ? "active" : ""}
                  onClick={() => setActiveTabName("banners")}
                >
                  Banners
                </Styled.AdServerTabTitle>
                <Styled.AdServerTabTitle
                  className={activeTabName === "rollads" ? "active" : ""}
                  onClick={() => setActiveTabName("rollads")}
                >
                  Roll Ads
                </Styled.AdServerTabTitle>
              </Styled.AdServerTabsContainer>

              {activeTabName === "rollads" && hasCampaigns && (
                <Styled.ButtonContainer>
                  <Button variant="primary" onClick={() => setShowModal(true)}>
                    New Campaign
                    <Styled.PlusIcon src={plusIcon} alt="Plus Icon" />
                  </Button>
                </Styled.ButtonContainer>
              )}
            </Styled.WelcomeHeaderWrapper>
          </Row>

          <Styled.AdServerItemRow>
            <Col>
              {loading ? (
                <Loader />
              ) : error ? (
                <Error
                  message={error}
                />
              ) : activeTabName === "banners" ? (
                <AdServerItems />
              ) : (
                <RollAds handleDeleteCampaign={handleDeleteCampaign} campaigns={campaigns} fetchCampaigns={fetchCampaigns} loading={loading} />
              )}
            </Col>
          </Styled.AdServerItemRow>
        </Styled.AdDetailsCol>

        {activeTabName === "banners" && (
          <Styled.PreviewWrapper md={4}>
            <Styled.PreviewHeader>Preview</Styled.PreviewHeader>
            <Image src={AdPreviewImage} width={250} />
          </Styled.PreviewWrapper>
        )}
      </Styled.AdServerRow>

      {showModal && <RollAdsModal show={showModal} onClose={() => setShowModal(false)} campaignId={selectedCampaignId} fetchCampaigns={fetchCampaigns}/>}
    </Styled.AdServerContainer>
  );
};
